import { useSocialLinks } from 'app/hooks/use-social-links';
import { IconUserPlus } from '@tabler/icons-react-native';
import { ShowErrors } from 'app/components/show-errors';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { useNavigation } from 'app/hooks/navigation';
import { localeAwareFormat } from 'app/lib/date';
import { useDismissTrustInvitation } from 'app/network/notification';
import { useAddTrustedMember, useProfile } from 'app/network/profile';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

export function NotificationTrustAdded({
  notificationId,
  userId,
  dismissed,
  createdAt,
  picture,
  title,
  titleArgs,
}: {
  notificationId?: string;
  userId: string;
  dismissed: boolean;
  createdAt: string;
  picture?: string;
  title: string;
  titleArgs?: Record<string, unknown>;
}) {
  const { t } = useTranslation('notifications');
  const navigation = useNavigation();
  const socialLinks = useSocialLinks();
  const profile = useProfile(userId, { enabled: socialLinks.enabled });
  const add = useAddTrustedMember();
  const decline = useDismissTrustInvitation();
  const [resolved, setResolved] = useState(false);
  const isTrusted = profile.data?.trust?.isTrustedByViewer;
  const showActions = !dismissed && !resolved && !isTrusted;
  const busy = add.isPending || decline.isPending;
  if (!socialLinks.enabled) return null;
  return (
    <Row padding="spacing.general.x4" gap="spacing.general.x4" alignItems="flex-start">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('trustAdded.viewProfile')}
        onPress={() => navigateToUserProfile(navigation, userId)}
      >
        <Thumbnail height={56} source={{ uri: picture }} />
      </Pressable>
      <Column flex={1} gap="spacing.general.x2">
        <Text variant="bodyBase" fontWeight="600">
          {t(title, titleArgs)}
        </Text>
        <Text variant="bodySmall">{t('trustAdded.message')}</Text>
        <Text variant="bodySmall" color="text.secondary">
          {localeAwareFormat(new Date(createdAt), 'yyyy-MM-dd')}
        </Text>
        {showActions ? (
          <Row flexWrap="wrap" gap="spacing.general.x2">
            <Button
              accessibilityRole="button"
              variant="primary"
              minHeight={40}
              paddingHorizontal="spacing.general.x3"
              paddingVertical="spacing.general.x2"
              style={{ borderRadius: 1000 }}
              disabled={busy || !profile.data || profile.isFetching}
              loading={add.isPending}
              onPress={() => add.mutate(userId, { onSuccess: () => setResolved(true) })}
            >
              <Row alignItems="center" gap="spacing.general.x2">
                <IconUserPlus size={20} color="white" strokeWidth={ICON_STROKE_WIDTH} />
                <Text color="text.inverse.primary" variant="bodySmall" fontWeight="600">
                  {t('trustAdded.add')}
                </Text>
              </Row>
            </Button>
            <Button
              accessibilityRole="button"
              variant="secondary"
              minHeight={40}
              paddingHorizontal="spacing.general.x3"
              paddingVertical="spacing.general.x2"
              style={{ borderRadius: 1000 }}
              disabled={busy || !notificationId}
              loading={decline.isPending}
              onPress={() => {
                if (notificationId) decline.mutate(notificationId, { onSuccess: () => setResolved(true) });
              }}
            >
              <Text color="text.emphasized" variant="bodySmall" fontWeight="600">
                {t('trustAdded.decline')}
              </Text>
            </Button>
          </Row>
        ) : null}
        <ShowErrors error={profile.error ?? add.error ?? decline.error} />
      </Column>
    </Row>
  );
}
